import { useEffect, useState } from 'react';
import { DownloadSimple, DeviceMobile, X, Share, Plus, Browser } from '@phosphor-icons/react';

// LocalStorage-key zodat dismiss persistent is
const DISMISS_KEY = 'billfac_install_dismissed_at';
const DISMISS_DAYS = 14;  // herinner opnieuw na 14 dagen

/**
 * "Installeer app"-knop voor PWA-installatie.
 * - Android/Chrome/Edge: native install prompt via beforeinstallprompt event
 * - iOS Safari: modal met handmatige instructies
 * - Desktop Chrome/Edge: kan ook gebruikt worden als bookmarklet
 *
 * Verbergt zichzelf automatisch als:
 *   1. App is al geïnstalleerd (display-mode: standalone)
 *   2. Gebruiker heeft binnen `DISMISS_DAYS` op "Niet nu" geklikt
 *
 * Props:
 *   variant: 'compact' (sidebar) of 'banner' (full-width prompt)
 */
export default function InstallAppButton({ variant = 'compact' }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Detecteer platform
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /Android/i.test(ua);
  const isMobile = isIOS || isAndroid || (typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches);

  useEffect(() => {
    // Check of de app al draait als geïnstalleerde PWA
    const checkStandalone = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches
        || window.navigator.standalone === true;
      setIsStandalone(standalone);
    };
    checkStandalone();

    // Check dismiss-tijd uit localStorage
    try {
      const stored = localStorage.getItem(DISMISS_KEY);
      if (stored) {
        const dismissedAt = parseInt(stored, 10);
        const daysAgo = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
        if (daysAgo < DISMISS_DAYS) setDismissed(true);
      }
    } catch (_e) { /* niks */ }

    // Beforeinstallprompt — Android/Chrome/Edge
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // Cleanup als app geïnstalleerd wordt
    const onInstalled = () => {
      setDeferredPrompt(null);
      setIsStandalone(true);
    };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      // Android/desktop: native prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
      } else {
        handleDismiss();
      }
    } else {
      // iOS of geen prompt event → toon handmatige instructies
      setShowModal(true);
    }
  };

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, Date.now().toString()); } catch (_e) { /* niks */ }
    setDismissed(true);
    setShowModal(false);
  };

  // Verberg als al geïnstalleerd of gedismissed
  if (isStandalone || dismissed) return null;
  // Compact-variant: altijd tonen (sidebar) — gebruikers op alle devices kunnen installeren of instructies bekijken
  // Banner-variant: enkel op mobile of als beforeinstallprompt is gefired (niet opdringerig op desktop)
  if (variant === 'banner' && !isMobile && !deferredPrompt) return null;

  // ─── RENDER ─────────────────────────────────
  return (
    <>
      {variant === 'compact' ? (
        <button
          type="button"
          onClick={handleInstall}
          className="w-full px-3 py-2 mt-2 rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-xs font-semibold inline-flex items-center justify-center gap-1.5 shadow-sm transition-all"
          data-testid="install-app-btn-compact"
          title="Installeer BillFac als app op je telefoon"
        >
          <DeviceMobile size={14} weight="bold" />
          Installeer app
        </button>
      ) : (
        <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl p-4 shadow-lg flex items-center gap-3 mb-3" data-testid="install-app-banner">
          <DeviceMobile size={28} weight="duotone" className="flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-bold">Zet BillFac op je startscherm</div>
            <div className="text-xs opacity-90">In 1 tik bereikbaar zoals een echte app</div>
          </div>
          <button
            type="button"
            onClick={handleInstall}
            className="bg-white text-emerald-700 px-4 py-2 rounded-lg font-semibold text-sm hover:bg-emerald-50 transition-colors flex-shrink-0"
            data-testid="install-app-btn-banner"
          >
            Installeer
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="text-white/70 hover:text-white p-1 flex-shrink-0"
            title="Niet nu"
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* iOS handmatige instructies modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-end sm:items-center justify-center sm:p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="install-app-modal">
            {/* Header */}
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 text-white p-6 sm:rounded-t-2xl relative">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="absolute top-3 right-3 text-white/70 hover:text-white"
              >
                <X size={22} />
              </button>
              <DeviceMobile size={40} weight="duotone" className="mb-2" />
              <h2 className="text-xl font-bold">Installeer BillFac</h2>
              <p className="text-sm text-white/90 mt-1">In 2 tikken op je startscherm — werkt zoals een echte app, ook offline.</p>
            </div>

            {/* Stappen */}
            <div className="p-6 space-y-4">
              {isIOS ? (
                <>
                  <Step
                    number="1"
                    icon={<Share size={22} weight="fill" className="text-blue-500" />}
                    title="Tik op de Deel-knop"
                    body={<>Onderaan Safari (vierkantje met pijl omhoog <Share size={14} weight="fill" className="inline" />).</>}
                  />
                  <Step
                    number="2"
                    icon={<Plus size={22} weight="bold" className="text-emerald-600" />}
                    title='Tik op "Zet op beginscherm"'
                    body="Soms moet je naar beneden scrollen in het Deel-menu."
                  />
                  <Step
                    number="3"
                    icon={<DeviceMobile size={22} weight="fill" className="text-purple-600" />}
                    title='Tik op "Voeg toe" rechtsboven'
                    body="Het BillFac-logo verschijnt nu op je beginscherm — klaar!"
                  />
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
                    ⚠️ Werkt enkel op <strong>Safari</strong>. In Chrome of Firefox op iOS is dit (nog) niet mogelijk.
                  </div>
                </>
              ) : isAndroid ? (
                <>
                  <Step
                    number="1"
                    icon={<Browser size={22} weight="fill" className="text-blue-500" />}
                    title="Tik op het menu (⋮)"
                    body="Rechtsboven in Chrome — de drie verticale puntjes."
                  />
                  <Step
                    number="2"
                    icon={<Plus size={22} weight="bold" className="text-emerald-600" />}
                    title='Tik op "App installeren" of "Toevoegen aan startscherm"'
                    body="De naam kan licht verschillen per Chrome-versie."
                  />
                  <Step
                    number="3"
                    icon={<DeviceMobile size={22} weight="fill" className="text-purple-600" />}
                    title='Bevestig met "Installeren"'
                    body="BillFac verschijnt op je startscherm en in je app-lade."
                  />
                </>
              ) : (
                <>
                  <Step
                    number="1"
                    icon={<Browser size={22} weight="fill" className="text-blue-500" />}
                    title="Klik op het install-icoon in de adresbalk"
                    body="In Chrome of Edge — rechts naast het URL-veld. Soms verborgen onder ⋮ → 'App installeren'."
                  />
                  <Step
                    number="2"
                    icon={<DeviceMobile size={22} weight="fill" className="text-purple-600" />}
                    title="Bevestig de installatie"
                    body="BillFac start nu in een eigen venster, zonder browser-tabs."
                  />
                </>
              )}

              <button
                type="button"
                onClick={handleDismiss}
                className="w-full mt-2 text-center text-sm text-slate-500 hover:text-slate-700 underline"
              >
                Niet nu, herinner me later
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Step({ number, icon, title, body }) {
  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-600">
        {number}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 font-semibold text-slate-900">
          {icon}
          <span>{title}</span>
        </div>
        <div className="text-xs text-slate-600 mt-0.5 leading-relaxed">{body}</div>
      </div>
    </div>
  );
}
